import { useTranslation } from 'react-i18next';

import {
    type AuditFilterChange,
    type AuditLogEntry,
    addAuditFilters,
    auditMessage,
    useAuditText,
} from '@/domain/auditLog';

import CellFilterButton from './CellFilterButton';
import CellLines from './CellLines';

import styles from './OptionalCells.module.scss';

// Enough to tell a request apart from its neighbours; the whole id is in the title.
const REQUEST_ID_LENGTH = 8;

interface EntryCellProps {
    entry: AuditLogEntry;
}

interface FilterCellProps extends EntryCellProps {
    onFilter: (change: AuditFilterChange) => void;
}

export function RequestCell({ entry }: EntryCellProps) {
    const { t } = useTranslation();
    const render = useAuditText();
    const { method, route } = entry;
    if (method === null && route === null) return <CellLines first={t('auditLog.table.noValue')} />;
    const title =
        method === null || route === null
            ? (method ?? route ?? undefined)
            : render(auditMessage('auditLog.summary.request', { method, route }));
    return (
        <CellLines
            first={
                <span className={styles.request}>
                    {method === null ? null : <span className={styles.method}>{method}</span>}
                    {route === null ? null : <span className={styles.route}>{route}</span>}
                </span>
            }
            firstTitle={title}
        />
    );
}

export function RequestIdCell({ entry, onFilter }: FilterCellProps) {
    const { t } = useTranslation();
    const { requestId } = entry;
    if (requestId === null) return <CellLines first={t('auditLog.table.noValue')} />;
    return (
        <CellLines
            numeric
            first={
                <CellFilterButton
                    value={requestId}
                    onFilter={() => onFilter(addAuditFilters({ requestId }))}>
                    {requestId.slice(0, REQUEST_ID_LENGTH)}
                </CellFilterButton>
            }
            firstTitle={requestId}
        />
    );
}

export function UserAgentCell({ entry }: EntryCellProps) {
    const { t } = useTranslation();
    const { userAgent } = entry;
    return (
        <CellLines
            first={userAgent ?? t('auditLog.table.noValue')}
            firstTitle={userAgent ?? undefined}
        />
    );
}

export function EntryIdCell({ entry }: EntryCellProps) {
    return <CellLines numeric first={entry.id} firstTitle={entry.id} />;
}
